/**
 * Mapping card (Phase 65 / BLU-594 PR2) — one of Product types / Colours /
 * Sizes on the Code tables tab.
 *
 * Unmapped values (from the coverage scan, filtered to those NOT yet in the
 * buffer) sit in an amber block at the top with a pre-suggested code + Accept;
 * accepting writes the buffer, so the row drops out of "unmapped" and fades
 * into the mapped list (220ms fade-in, DESIGN §5, motion-reduce safe) and the
 * coverage meter updates optimistically. Mapped codes are click-to-edit
 * (validated inline against the write-path regex). Admin-gated writes mirror
 * RipRoutingSection; coordinators see read-only tables.
 *
 * Usage counts on MAPPED rows come from the coverage `unmappedValues` tally, so
 * they are present for values accepted THIS session and absent for values the
 * server already had mapped (the coverage contract carries per-value counts
 * only for unmapped values). Shown when known, omitted otherwise.
 *
 * Colours of a store SKU org (86-22): once the scan has finished, an unmapped
 * colour whose store SKUs all use one colour code is pre-filled with that
 * code under "From your SKUs"; one seen with two or more codes is never
 * pre-filled, and lists each code with its count as a pick-list. While the
 * scan is still streaming nothing is pre-filled: every code seen so far is
 * listed as a pick-list, so a second code on a later page is never hidden
 * behind a pre-fill. A store SKU org never gets a guessed `suggestCode` (its
 * codes are its SKUs' own; the input stays blank). Accept still only writes
 * the buffer; the admin save is the only write. Other orgs' rows carry no SKU
 * codes and show today's `suggestCode` suggestion.
 *
 * Product types read their kind through the shared custom rule
 * (`productTypeKind`, BLU-1373). A Mixed type shows a plain "mixed" chip in
 * place of the custom toggle (its products are set one by one in the Products
 * tab). An org that sells custom only shows every type as custom with the
 * CUST prefix and no toggle, under one note saying why. Otherwise the custom
 * toggle is today's, writing the same list.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, Info, Pencil, Plus, Ruler, Wand2, X } from 'lucide-react';
import { productTypeKind, type ProductTypeKind } from '@threadheads/shared';
import { cn } from '@/lib/utils';
import { NoteLine } from '@/components/shared/NoteLine';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CoverageBar } from './CoverageBar';
import { ColourDot } from './ColourDot';
import { isValidCode, suggestCode } from '../lib/code-suggest';
import type { CodeTable, ProductSyncCoverage } from '../lib/types';
import type { ProductSyncSettingsBuffer } from '../hooks/useProductSyncSettingsBuffer';

interface MappingCardProps {
  table: CodeTable;
  segKey: string;
  title: string;
  exampleCode: string;
  coverage: ProductSyncCoverage | null;
  buffer: ProductSyncSettingsBuffer;
  admin: boolean;
  onHoverSegment: (segKey: string | null) => void;
  /** Value name to focus (from a cross-tab "Map it"); consumed once. */
  focusName: string | null;
  onFocusConsumed: () => void;
  /**
   * BLU-790: why there is no coverage to show, or `null` when there is.
   * 'scanning' = pages still arriving; 'unavailable' = the scan failed.
   *
   * Must cover BOTH: an earlier cut keyed only on "loading", so a FAILED scan
   * fell through to the real label and rendered "0 of 0 mapped" with a full
   * bar — absence reading as "fully mapped", the exact bug the pending state
   * exists to prevent. Caught on the local rig, not by any unit test.
   */
  coverageMissing: 'scanning' | 'unavailable' | null;
}

function slug(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
}
function unmappedInputId(table: CodeTable, name: string): string {
  return `psync-code-${table}-${slug(name)}`;
}
function hasName(map: Record<string, string>, name: string): boolean {
  const key = name.trim().toLowerCase();
  return Object.keys(map).some((k) => k.trim().toLowerCase() === key);
}

export function MappingCard(props: MappingCardProps) {
  const { table, segKey, title, exampleCode, coverage, buffer, admin, onHoverSegment, focusName, onFocusConsumed, coverageMissing } = props;
  const { t } = useTranslation('productSync');

  const codeMap = buffer.settings[table];
  // Product types only: each row's kind under the shared custom rule. Custom
  // only makes every type custom, so no per-type toggle can apply there.
  const isProductTypes = table === 'productTypes';
  const allCustom = isProductTypes && buffer.settings.sells === 'custom';
  const kindOf = (name: string): ProductTypeKind => (isProductTypes ? productTypeKind(buffer.settings, name) : 'readyMade');

  // Usage-by-name (lowercased) for mapped rows accepted this session. Reads
  // `coverage` directly so the memo deps stay stable (the `?? []` fallback would
  // be a fresh array each render).
  const usageByName = useMemo(() => {
    const m = new Map<string, number>();
    for (const v of coverage?.unmappedValues[table] ?? []) m.set(v.name.toLowerCase(), v.count);
    return m;
  }, [coverage, table]);

  // 86-22 review: SKU codes pre-fill only once the scan has finished, and a
  // store SKU org never gets a guessed code.
  const scanDone = coverage !== null && !coverage.partial;
  const storeSkuOrg = coverage?.storeSkuOrg === true;

  const unmappedAll = coverage?.unmappedValues[table] ?? [];
  const unmapped = unmappedAll.filter((v) => !hasName(codeMap, v.name));
  const mappedEntries = Object.entries(codeMap).sort((a, b) => a[0].localeCompare(b[0]));

  // Colours card only: does the colour-less code collide with a mapped colour's
  // code? Not blocking (both are legal), but colour-less products would then
  // share that colour's RIP routing, so surface it quietly.
  const colourlessCode = buffer.settings.colourlessCode;
  const colourlessClashName =
    table === 'colours'
      ? Object.entries(buffer.settings.colours).find(([, code]) => code.toUpperCase() === colourlessCode.trim().toUpperCase())?.[0]
      : undefined;

  const total = coverage?.mappedCoverage[table].total ?? 0;
  const mappedCount = Math.max(0, total - unmapped.length);
  // BLU-790: with no coverage this used to compute total=0, unmapped=[], so
  // `complete=true` and `pct=100` — a FULL bar reading "0 of 0 mapped".
  // Absence of the scan must never render as "fully mapped" (DESIGN §15.2 also
  // bans a bare "0 of 0"). Reachable now that the scan streams behind the tab
  // rather than blocking it — via BOTH the scanning and the failed path.
  const complete = coverageMissing === null && unmapped.length === 0;
  const pct = coverageMissing !== null ? 0 : total > 0 ? (mappedCount / total) * 100 : 100;
  const coverageLabel =
    coverageMissing === 'scanning'
      ? t('mapping.card.coveragePending')
      : coverageMissing === 'unavailable'
        ? t('mapping.card.coverageUnavailable')
        : t('mapping.card.coverage', { mapped: mappedCount, count: total });

  // Focus a value's input after a cross-tab "Map it".
  useEffect(() => {
    if (!focusName) return;
    const id = unmappedInputId(table, focusName);
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        (el as HTMLInputElement).focus();
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
      }
      onFocusConsumed();
    });
    return () => cancelAnimationFrame(raf);
  }, [focusName, table, onFocusConsumed]);

  return (
    <div
      className="flex flex-col overflow-hidden rounded-xl border bg-background transition-colors duration-200 hover:border-threadheads-purple/30 motion-reduce:transition-none"
      onMouseEnter={() => onHoverSegment(segKey)}
      onMouseLeave={() => onHoverSegment(null)}
      onFocus={() => onHoverSegment(segKey)}
      onBlur={() => onHoverSegment(null)}
    >
      <header className="border-b px-4 pb-3 pt-3.5">
        <div className="flex items-center gap-2 text-[13.5px] font-bold">
          {title}
          <span className="rounded bg-threadheads-purple/10 px-1.5 py-0.5 font-mono text-[10px] font-bold text-threadheads-purple">
            {exampleCode}
          </span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <CoverageBar value={pct} complete={complete} label={coverageLabel} className="flex-1" />
          <span className="whitespace-nowrap text-[11px] font-semibold tabular-nums text-muted-foreground">
            {coverageLabel}
          </span>
        </div>
      </header>

      <div className="flex flex-col gap-0.5 px-2 py-2.5">
        {allCustom && (
          <NoteLine icon={Info} className="px-2 pb-1.5">
            {t('mapping.allCustomNote')}
          </NoteLine>
        )}
        {/* Unmapped block */}
        {unmapped.length > 0 && (
          <div className="mb-1 rounded-lg border border-amber-300 bg-amber-50 p-2">
            <p className="flex items-center gap-1.5 px-1 pb-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-800">
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              {t('mapping.unmapped.header')}
            </p>
            {unmapped.map((v) => (
              <UnmappedRow
                key={v.name}
                table={table}
                name={v.name}
                count={v.count}
                admin={admin}
                isProductType={isProductTypes}
                kind={kindOf(v.name)}
                allCustom={allCustom}
                onToggleCustom={() => buffer.toggleCustomType(v.name)}
                onAccept={(code) => buffer.setCode(table, v.name, code)}
                skuCodes={table === 'colours' ? v.skuCodes : undefined}
                storeSkuOrg={storeSkuOrg}
                scanDone={scanDone}
              />
            ))}
          </div>
        )}

        {/* Mapped rows */}
        {mappedEntries.map(([name, code]) => (
          <MappedRow
            key={name}
            name={name}
            code={code}
            usage={usageByName.get(name.toLowerCase())}
            table={table}
            admin={admin}
            isColour={table === 'colours'}
            isProductType={table === 'productTypes'}
            isSingleSize={buffer.settings.singleSizeTypes.some((s) => s.toLowerCase() === name.toLowerCase())}
            kind={kindOf(name)}
            allCustom={allCustom}
            onEdit={(next) => buffer.setCode(table, name, next)}
            onToggleSingleSize={() => buffer.toggleSingleSizeType(name)}
            onToggleCustom={() => buffer.toggleCustomType(name)}
          />
        ))}

        {/* Add manual entry */}
        {admin && <AddRow table={table} buffer={buffer} />}
      </div>

      {/* Colours footer: colour-less code + dark-set summary */}
      {table === 'colours' && (
        <footer className="space-y-2.5 border-t px-4 py-2.5">
          {/* Colour-less placeholder code — products with no colour option */}
          <div className="border-b pb-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex-1 text-[11px] font-semibold text-muted-foreground">{t('mapping.colourlessLabel')}</span>
              <EditableCodeChip
                code={colourlessCode}
                name={t('mapping.colourlessName')}
                table="colours"
                admin={admin}
                onCommit={(next) => buffer.setColourlessCode(next)}
              />
            </div>
            {colourlessClashName && (
              <p className="mt-1.5 text-[11px] text-amber-700">{t('mapping.colourlessClash', { name: colourlessClashName })}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold text-muted-foreground">{t('mapping.darkSetLabel')}</span>
            {buffer.settings.darkColours.length === 0 ? (
              <span className="text-[11px] text-muted-foreground/70">{t('mapping.darkSetEmpty')}</span>
            ) : (
              buffer.settings.darkColours.map((name) => (
                <span key={name} className="inline-flex items-center gap-1.5 rounded-full bg-[#2A2733] py-0.5 pl-1.5 pr-1 text-[11px] font-medium text-white">
                  <ColourDot name={name} />
                  {name}
                  {admin && (
                    <button
                      type="button"
                      onClick={() => buffer.toggleDarkColour(name, false)}
                      aria-label={t('tier2.remove', { slot: name })}
                      className="grid h-4 w-4 place-items-center rounded-full text-white/70 transition-colors duration-200 hover:bg-white/20 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 motion-reduce:transition-none"
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  )}
                </span>
              ))
            )}
          </div>
        </footer>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// On-demand-custom affordance (product types only): the same chip geometry as
// the single-size toggle, but purple when active because "wraps with CUST-" is
// a real state change, not neutral metadata. Shared by mapped + unmapped rows.
// ---------------------------------------------------------------------------

function CustomTypeToggle({ active, onToggle }: { active: boolean; onToggle: () => void }) {
  const { t } = useTranslation('productSync');
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 motion-reduce:transition-none',
        active ? 'bg-threadheads-purple/10 text-threadheads-purple' : 'bg-muted text-muted-foreground hover:text-foreground',
      )}
      title={t('mapping.customToggle')}
    >
      <Wand2 className="h-2.5 w-2.5" aria-hidden="true" />
      {t('mapping.customChip')}
    </button>
  );
}

function CustomTypeBadge() {
  const { t } = useTranslation('productSync');
  return (
    <span className="shrink-0 rounded bg-threadheads-purple/10 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-threadheads-purple">
      {t('mapping.customChip')}
    </span>
  );
}

/**
 * A Mixed type: not interactive here, because each of its products is set
 * Custom or Ready-made on its own in the Products tab. Same geometry as the
 * custom badge, neutral tone (a state, not a promotion).
 */
function MixedTypeChip() {
  const { t } = useTranslation('productSync');
  return (
    <span
      className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground"
      title={t('mapping.mixedHint')}
    >
      {t('mapping.mixedChip')}
    </span>
  );
}

/**
 * The custom affordance of one product type row. Mixed shows its chip and
 * custom only shows the badge, for everyone; otherwise an admin gets the
 * toggle and anyone else the badge when it is custom (mapped rows only).
 */
function TypeKindControl({ kind, allCustom, admin, showReadOnlyBadge, onToggle }: {
  kind: ProductTypeKind; allCustom: boolean; admin: boolean; showReadOnlyBadge: boolean; onToggle: () => void;
}) {
  if (kind === 'mixed') return <MixedTypeChip />;
  if (allCustom) return <CustomTypeBadge />;
  if (admin) return <CustomTypeToggle active={kind === 'custom'} onToggle={onToggle} />;
  return showReadOnlyBadge && kind === 'custom' ? <CustomTypeBadge /> : null;
}

/** The `CUST-` prefix badge shown before a custom type's code (SKU wrapper cue). */
function CustPrefix() {
  return (
    <span className="shrink-0 font-mono text-[11.5px] font-bold text-threadheads-purple" aria-hidden="true">
      CUST-
    </span>
  );
}

// ---------------------------------------------------------------------------
// Unmapped row: name + usage + pre-suggested code input + Accept.
// The name wraps, never truncates ("Heather Charcoal" and "Heather Cement"
// must not both read "Heather C…"); when the controls would leave it less
// than `basis-16`, they drop to their own line as one group.
// ---------------------------------------------------------------------------

/** A colour's SKU codes, most used first, then by code (numeric-looking keys would otherwise reorder). */
function sortedSkuCodes(skuCodes: Record<string, number> | undefined): Array<[code: string, count: number]> {
  if (!skuCodes) return [];
  return Object.entries(skuCodes).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

function UnmappedRow({ table, name, count, admin, isProductType, kind, allCustom, onToggleCustom, onAccept, skuCodes, storeSkuOrg, scanDone }: {
  table: CodeTable; name: string; count: number; admin: boolean;
  isProductType: boolean; kind: ProductTypeKind; allCustom: boolean; onToggleCustom: () => void; onAccept: (code: string) => void;
  /** 86-22: colours of a store SKU org only; the codes its own SKUs use for this value. */
  skuCodes?: Record<string, number>;
  /** 86-22 review: the org's store SKUs are its codes, so no guessed `suggestCode`. */
  storeSkuOrg: boolean;
  /** 86-22 review: the scan has seen every page; only then does a single SKU code pre-fill. */
  scanDone: boolean;
}) {
  const isCustom = kind === 'custom';
  const { t } = useTranslation('productSync');
  const fromSkus = sortedSkuCodes(skuCodes);
  // A single SKU code pre-fills only once the scan is done (a later page may
  // still bring a second code). Two or more, or any while scanning: nothing
  // pre-filled, the codes are listed to pick. No SKU codes: today's
  // suggestion, except for a store SKU org, whose input stays blank.
  const prefillFromSkus = scanDone && fromSkus.length === 1;
  const showSkuPicks = fromSkus.length > 1 || (fromSkus.length === 1 && !scanDone);
  const suggested = prefillFromSkus
    ? fromSkus[0]![0]
    : fromSkus.length > 0 || storeSkuOrg
      ? ''
      : suggestCode(name);
  const [typed, setTyped] = useState<string | null>(null);
  const code = typed ?? suggested;
  const setCode = (next: string) => setTyped(next);
  const [error, setError] = useState(false);
  const inputId = unmappedInputId(table, name);
  const hintId = `${inputId}-from-skus`;

  const accept = () => {
    const next = code.trim().toUpperCase();
    if (!isValidCode(next, table)) {
      setError(true);
      return;
    }
    onAccept(next);
  };

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md px-1 py-1">
      <span className="min-w-0 flex-1 basis-16 break-words text-[12.5px] font-semibold">{name}</span>
      {/* Before the input and Accept, in reading and tab order. */}
      {showSkuPicks && (
        <div className="flex basis-full flex-wrap items-center justify-end gap-1.5">
          <span className="text-[10.5px] font-medium text-amber-800">{t('mapping.unmapped.skuCodesLabel')}</span>
          {fromSkus.map(([skuCode, uses]) => (
            <button
              key={skuCode}
              type="button"
              disabled={!admin}
              aria-pressed={code.trim().toUpperCase() === skuCode}
              aria-label={t('mapping.unmapped.skuCodeOptionLabel', { code: skuCode, name, count: uses })}
              onClick={() => { setCode(skuCode); setError(false); }}
              className={cn(
                'inline-flex min-h-6 min-w-6 items-center justify-center rounded border border-amber-300 bg-background px-2 py-1 font-mono text-[10.5px] font-bold tabular-nums text-amber-900 transition-colors duration-200 hover:border-threadheads-purple/40 hover:text-threadheads-purple focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-60 motion-reduce:transition-none',
                code.trim().toUpperCase() === skuCode && 'border-threadheads-purple bg-threadheads-purple/10 text-threadheads-purple',
              )}
            >
              {t('mapping.unmapped.skuCodeOption', { code: skuCode, count: uses })}
            </button>
          ))}
        </div>
      )}
      <div className="ml-auto flex items-center gap-2">
        {isProductType && (
          <TypeKindControl kind={kind} allCustom={allCustom} admin={admin} showReadOnlyBadge={false} onToggle={onToggleCustom} />
        )}
        <span className="shrink-0 text-[10.5px] tabular-nums text-amber-700">{t('mapping.unmapped.usage', { count })}</span>
        {isProductType && isCustom && <CustPrefix />}
        <Input
          id={inputId}
          value={code}
          disabled={!admin}
          aria-label={t('mapping.unmapped.codeLabel', { name })}
          aria-describedby={prefillFromSkus ? hintId : undefined}
          aria-invalid={error || undefined}
          onChange={(e) => { setCode(e.target.value); setError(false); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); accept(); } }}
          className={cn('h-7 border-amber-300 bg-background text-center font-mono text-[11.5px] font-bold uppercase', isProductType ? 'w-24' : 'w-16', error && 'border-destructive')}
        />
        {admin && (
          <Button
            size="sm"
            className="h-7 gap-1 bg-threadheads-purple px-2.5 text-[11px] text-white hover:bg-threadheads-purple/90"
            onClick={accept}
          >
            {t('mapping.unmapped.accept')}
          </Button>
        )}
      </div>
      {prefillFromSkus && (
        <p className="basis-full text-right text-[10.5px] font-medium text-amber-800" title={t('mapping.unmapped.fromSkusHint', { name })}>
          {t('mapping.unmapped.fromSkus')}
          {/* The input's description; hidden so it is not read twice. */}
          <span id={hintId} hidden>{t('mapping.unmapped.fromSkusHint', { name })}</span>
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mapped row: name + usage + click-to-edit code chip (+ single-size toggle).
// ---------------------------------------------------------------------------

function MappedRow(props: {
  name: string; code: string; usage: number | undefined; table: CodeTable;
  admin: boolean; isColour: boolean; isProductType: boolean; isSingleSize: boolean; kind: ProductTypeKind; allCustom: boolean;
  onEdit: (code: string) => void; onToggleSingleSize: () => void; onToggleCustom: () => void;
}) {
  const { name, code, usage, admin, isColour, isProductType, isSingleSize, kind, allCustom, onEdit, onToggleSingleSize, onToggleCustom } = props;
  const isCustom = kind === 'custom';
  const { t } = useTranslation('productSync');

  return (
    <div className="flex items-center gap-2 rounded-md px-2 py-1.5 motion-safe:animate-fade-in hover:bg-muted/50">
      <span className="flex min-w-0 flex-1 items-center gap-2 text-[12.5px] font-medium">
        {isColour && <ColourDot name={name} />}
        <span className="truncate">{name}</span>
        {isProductType && admin && (
          <button
            type="button"
            onClick={onToggleSingleSize}
            aria-pressed={isSingleSize}
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 motion-reduce:transition-none',
              isSingleSize ? 'bg-threadheads-purple/10 text-threadheads-purple' : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
            title={t('mapping.singleSizeToggle')}
          >
            <Ruler className="h-2.5 w-2.5" aria-hidden="true" />
            {t('mapping.singleSizeChip')}
          </button>
        )}
        {isProductType && admin && (
          <TypeKindControl kind={kind} allCustom={allCustom} admin={admin} showReadOnlyBadge onToggle={onToggleCustom} />
        )}
        {isProductType && !admin && isSingleSize && (
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t('mapping.singleSizeChip')}
          </span>
        )}
        {isProductType && !admin && (
          <TypeKindControl kind={kind} allCustom={allCustom} admin={admin} showReadOnlyBadge onToggle={onToggleCustom} />
        )}
      </span>
      {usage != null && (
        <span className="shrink-0 text-[10.5px] tabular-nums text-muted-foreground/70">
          {t('mapping.unmapped.usage', { count: usage })}
        </span>
      )}
      <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground/40" aria-hidden="true" />
      {isProductType && isCustom && <CustPrefix />}
      <EditableCodeChip code={code} name={name} table={props.table} admin={admin} onCommit={onEdit} />
    </div>
  );
}

function EditableCodeChip({ code, name, table, admin, onCommit }: {
  code: string; name: string; table: CodeTable; admin: boolean; onCommit: (code: string) => void;
}) {
  const { t } = useTranslation('productSync');
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(code);
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);
  useEffect(() => { if (!editing) setValue(code); }, [code, editing]);

  const commit = () => {
    const next = value.trim().toUpperCase();
    if (!isValidCode(next, table)) { setError(true); return; }
    if (next !== code) onCommit(next);
    setEditing(false);
    setError(false);
  };

  if (!admin) {
    return <span className="rounded-md border bg-muted/60 px-2 py-1 font-mono text-[11.5px] font-bold">{code}</span>;
  }

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <Input
          ref={inputRef}
          value={value}
          aria-label={t('mapping.mapped.editLabel', { name })}
          aria-invalid={error || undefined}
          onChange={(e) => { setValue(e.target.value); setError(false); }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            if (e.key === 'Escape') { e.preventDefault(); setEditing(false); setError(false); }
          }}
          className={cn('h-7 text-center font-mono text-[11.5px] font-bold uppercase', table === 'productTypes' ? 'w-24' : 'w-16', error && 'border-destructive')}
        />
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={commit} className="grid h-6 w-6 place-items-center rounded text-emerald-700 hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1" aria-label={t('mapping.mapped.editLabel', { name })}>
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      aria-label={t('mapping.mapped.editLabel', { name })}
      className="group inline-flex items-center gap-1 rounded-md border bg-muted/60 px-2 py-1 font-mono text-[11.5px] font-bold transition-colors duration-200 hover:border-threadheads-purple/30 hover:text-threadheads-purple focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 motion-reduce:transition-none"
    >
      {code}
      <Pencil className="h-2.5 w-2.5 opacity-0 transition-opacity duration-200 group-hover:opacity-60 motion-reduce:transition-none" aria-hidden="true" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Add manual entry row.
// ---------------------------------------------------------------------------

function AddRow({ table, buffer }: { table: CodeTable; buffer: ProductSyncSettingsBuffer }) {
  const { t } = useTranslation('productSync');
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addLabel = table === 'productTypes' ? t('mapping.add.productType') : table === 'colours' ? t('mapping.add.colour') : t('mapping.add.size');

  const submit = () => {
    const cleanName = name.trim();
    const cleanCode = code.trim().toUpperCase();
    if (!cleanName) return;
    if (!isValidCode(cleanCode, table)) {
      setError(table === 'productTypes' ? t('mapping.invalidProductTypeCode') : t('mapping.invalidCode'));
      return;
    }
    if (Object.keys(buffer.settings[table]).some((k) => k.toLowerCase() === cleanName.toLowerCase())) {
      setError(t('mapping.duplicateName', { name: cleanName }));
      return;
    }
    buffer.setCode(table, cleanName, cleanCode);
    setName(''); setCode(''); setError(null); setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-0.5 inline-flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-[12px] font-semibold text-muted-foreground transition-colors duration-200 hover:bg-muted/50 hover:text-threadheads-purple focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 motion-reduce:transition-none"
      >
        <Plus className="h-3 w-3" aria-hidden="true" />
        {addLabel}
      </button>
    );
  }

  return (
    <div className="mt-0.5 space-y-1 rounded-md border p-2">
      <div className="flex items-center gap-1.5">
        <Input value={name} placeholder={t('mapping.add.namePlaceholder')} onChange={(e) => { setName(e.target.value); setError(null); }} className="h-7 flex-1 text-[12px]" aria-label={t('mapping.add.namePlaceholder')} />
        <Input value={code} placeholder={t('mapping.add.codePlaceholder')} onChange={(e) => { setCode(e.target.value); setError(null); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }} className={cn('h-7 text-center font-mono text-[11.5px] font-bold uppercase', table === 'productTypes' ? 'w-24' : 'w-16')} aria-label={t('mapping.add.codePlaceholder')} />
        <Button size="sm" className="h-7 px-2.5 text-[11px]" onClick={submit} disabled={!name.trim() || !code.trim()}>{t('mapping.add.confirm')}</Button>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => { setOpen(false); setError(null); setName(''); setCode(''); }}>{t('mapping.add.cancel')}</Button>
      </div>
      {error && <p className="px-1 text-[11px] text-destructive" role="alert">{error}</p>}
    </div>
  );
}
